import { useState } from 'react'
import { Outlet, NavLink, Link, useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/authStore'
import { useFamilyStore } from '@/stores/familyStore'

export function AppLayout() {
  const navigate = useNavigate()
  const { user, signOut } = useAuthStore()
  const { activeChild, family } = useFamilyStore()
  const [menuOpen, setMenuOpen] = useState(false)

  async function handleSignOut() {
    await signOut()
    navigate('/auth/sign-in', { replace: true })
  }

  const navItems = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/activities', label: 'Activities' },
    { to: '/reading-list', label: 'Reading List' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/family', label: 'Family' },
  ]

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Top nav */}
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Birchfolio logo" className="h-11 w-auto" />
              <span style={{ fontFamily: "'Lora', serif", color: '#2C4E37', fontSize: '1.35rem', fontWeight: 600 }}>
                Birchfolio
              </span>
            </Link>
            {family && (
              <span className="hidden text-sm text-gray-500 sm:block">
                · {family.name}
              </span>
            )}
          </div>

          {/* Nav links — desktop */}
          <nav className="hidden gap-1 md:flex">
            {navItems.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-birch-100 text-birch-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Right side — desktop */}
          <div className="hidden items-center gap-3 md:flex">
            {activeChild && (
              <span className="text-sm text-gray-500">{activeChild.firstName}</span>
            )}
            <span className="text-sm font-medium text-gray-700">
              {user?.givenName ?? user?.email}
            </span>
            <button
              onClick={handleSignOut}
              className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              Sign out
            </button>
          </div>

          {/* Hamburger — mobile */}
          <button
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100 md:hidden"
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu"
          >
            {menuOpen ? (
              /* X icon */
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              /* Hamburger icon */
              <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="border-t border-gray-100 px-4 pb-4 md:hidden">
            <nav className="flex flex-col gap-1 pt-2">
              {navItems.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-birch-100 text-birch-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
              <span className="text-sm text-gray-500">
                {activeChild ? `${activeChild.firstName} · ` : ''}{user?.givenName ?? user?.email}
              </span>
              <button
                onClick={handleSignOut}
                className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="mx-auto w-full min-w-0 max-w-6xl flex-1 overflow-x-hidden px-4 py-8">
        <Outlet />
      </main>

      <footer className="border-t border-gray-200 py-4 text-center text-xs text-gray-400">
        Birchfolio &copy; {new Date().getFullYear()}
      </footer>
    </div>
  )
}
